import PostService from '../../services/PostService';
import TaskTrackingService from '../../services/TaskTrackingService';
import { withAccessConfirmation } from './confirmAccess';
import { contentFieldsFrom, durationError, formatDuration, platformChoices, sourceUrlError } from './taskFields';
import { fileWord, planFolderLine, POST_SEND_BACK_REASONS, SEND_BACK_REASONS, shortName } from './taskUi';
import { refreshTaskSummary } from './useTaskSummary';

/**
 * What a PM can do from the drawer: the menu, moving between stages and the posting date. Each is
 * the settings for a TaskActionDialog, opened with openDialog.
 */
const usePmActions = ({
    detail,
    perms,
    current,
    link,
    waiting,
    noFile,
    categories,
    platforms,
    openDialog,
    setEditingPost,
    setSettingDate,
    onChanged,
    onClose,
}) => {
    const task = detail?.task;
    const stages = detail?.stages || [];
    const v = () => ({ version: task.version });
    const isPost = task?.contentType === 'POST';
    const isAudio = task?.contentType === 'AUDIO';
    const isDone = (s) => s.status === 'COMPLETED' || s.status === 'SKIPPED';
    const notDone = stages.filter((s) => !isDone(s));
    const earlier = (current ? stages.filter((s) => s.order < current.order) : stages).filter(
        (s) => !(isPost && s.status === 'SKIPPED'),
    );
    const avMayHaveFile = !!detail?.posting?.ready;

    const openSendBack = (stageKey) =>
        openDialog({
            usageName: 'TaskSendBackDialog',
            title: 'Send back',
            description: avMayHaveFile
                ? detail.posting.posted
                    ? 'This has already gone out. AV gets an email that it\'s on hold.'
                    : 'AV may already have this file. They get an email that it\'s on hold.'
                : 'Earlier work needs redoing. The stages after it reopen.',
            submitLabel: 'Send back',
            submitColor: 'warning',
            fields: [
                {
                    name: 'stageKey',
                    label: 'Send back to',
                    type: 'select',
                    options: earlier.map((s) => ({ value: s.stageKey, label: s.displayName })),
                    initial: stageKey,
                    required: true,
                },
                {
                    name: 'reasonCode',
                    label: 'Reason',
                    type: 'select',
                    options: isPost ? POST_SEND_BACK_REASONS : SEND_BACK_REASONS,
                    required: true,
                },
                { name: 'note', label: 'What needs redoing', multiline: true, required: true },
                { name: 'assignee', label: 'Assign to (defaults to whoever did it)', type: 'user' },
                {
                    name: 'alsoOtherLanguages',
                    label: 'Also send back the other languages of this file that are at or past this stage',
                    type: 'checkbox',
                },
                {
                    // Sending a linked stage back when the editor shows Published (S1)
                    name: 'publishedChoice',
                    type: 'radio',
                    initial: 'keep',
                    hidden: (vals) => !link?.published || !link.linkedStages?.[vals.stageKey],
                    label: `${task.languageLabel} is Published in the editor.`,
                    options: [
                        {
                            value: 'keep',
                            label: (vals) => {
                                const target = stages.find((s) => s.stageKey === vals.stageKey);
                                const who = vals.assignee || target?.assignedTo || target?.plannedAssignee;
                                return `Keep it Published. Mark ${target?.displayName || 'the stage'} as needing rework. ${
                                    who ? `${shortName(who)} can` : 'The translator can'
                                } edit now; changes show in exports right away.`;
                            },
                        },
                        {
                            value: 'move',
                            label: (vals) =>
                                `Move the editor back to ${link?.linkedStages?.[vals.stageKey] || 'that level'} in progress. It stops showing as Published, which affects exports, reels and glossary.`,
                        },
                    ],
                },
            ],
            submit: ({ publishedChoice, ...vals }) =>
                withAccessConfirmation((grant) =>
                    TaskTrackingService.override(task.id, {
                        ...v(),
                        type: 'SEND_BACK',
                        ...vals,
                        assignee: vals.assignee || undefined,
                        grantContentAccess: grant,
                        keepPublished: publishedChoice !== 'move',
                    }),
                ),
        });

    const openMoveForward = (target) => {
        const skipped = stages.filter((s) => s.order >= current.order && s.order < target.order && !isDone(s));
        const who = target.plannedAssignee || target.assignedTo;
        openDialog({
            usageName: 'TaskSkipToStageDialog',
            title: `Move to ${target.displayName}?`,
            description: `These stages will be marked skipped: ${skipped.map((s) => s.displayName).join(', ')}.`,
            submitLabel: 'Move',
            fields: [
                {
                    name: 'assignee',
                    label: who ? `Assign to (defaults to ${shortName(who)})` : 'Assign to',
                    type: 'user',
                },
                { name: 'note', label: 'Why (optional)', multiline: true },
            ],
            submit: (vals) =>
                withAccessConfirmation((grant) =>
                    TaskTrackingService.override(task.id, {
                        ...v(),
                        type: 'MOVE_TO',
                        stageKey: target.stageKey,
                        assignee: vals.assignee || undefined,
                        note: vals.note || undefined,
                        grantContentAccess: grant,
                    }),
                ),
        });
    };

    // What clicking a stage (or dropping a board card on it) does, or null when it can't move there
    const moveFor = (stage) => {
        if (!perms.canManage || !task || task.overallStatus === 'CANCELLED' || waiting) return null;
        if (current && stage.order > current.order) {
            return { label: 'Move here', go: () => openMoveForward(stage) };
        }
        if (earlier.some((s) => s.stageKey === stage.stageKey)) {
            return { label: 'Send back here', go: () => openSendBack(stage.stageKey) };
        }
        return null;
    };

    const openTargetDate = () =>
        openDialog({
            usageName: 'TaskPostingDateDialog',
            title: task.targetDate ? 'Change posting date' : 'Set posting date',
            description: waiting
                ? `For ${task.languageLabel} only. Stage due dates are worked out from it once the ${fileWord(task.contentType)} is linked.`
                : `For ${task.languageLabel} only. Due dates of stages not done yet are recalculated so the last stage finishes by this date.`,
            submitLabel: 'Save',
            fields: [
                {
                    name: 'targetDate',
                    label: 'Posting date',
                    type: 'date',
                    initial: task.targetDate || '',
                    required: true,
                },
            ],
            submit: (vals) => TaskTrackingService.update(task.id, { ...v(), targetDate: vals.targetDate }),
        });

    // One date for every language of a post (PMs, admins, and AV once it's done); a task's own elsewhere
    const canSetPostDate = !!detail?.post?.canSetPostingDate;
    const openPostingDate = isPost
        ? () => {
              setSettingDate(true);
          }
        : openTargetDate;

    // Nobody to take it from yet: a waiting task's first stage has a planned person at most
    const firstAssign = waiting ? !notDone[0]?.assignedTo && !notDone[0]?.plannedAssignee : !!current && !current.assignedTo;
    const assignLabel = firstAssign ? 'Assign' : 'Reassign';

    const pmActions = [
        {
            label: assignLabel,
            open: () =>
                openDialog({
                    usageName: 'TaskAssignDialog',
                    title: assignLabel,
                    description: waiting ? `They'll hear when the ${fileWord(task.contentType)} is linked and their stage comes up.` : undefined,
                    submitLabel: assignLabel,
                    fields: [
                        {
                            name: 'stageKey',
                            label: 'Stage',
                            type: 'select',
                            options: notDone.map((s) => ({ value: s.stageKey, label: s.displayName })),
                            initial: waiting ? notDone[0]?.stageKey : task.currentStageKey,
                            required: true,
                        },
                        { name: 'assignee', label: 'Assign to', type: 'user', required: true },
                        { name: 'note', label: 'Note (optional)', multiline: true },
                    ],
                    submit: (vals) =>
                        withAccessConfirmation((grant) =>
                            TaskTrackingService.override(task.id, {
                                ...v(),
                                type: 'REASSIGN',
                                ...vals,
                                grantContentAccess: grant,
                            }),
                        ),
                }),
        },
        {
            label: 'Edit details',
            open: () =>
                openDialog({
                    usageName: 'TaskEditDetailsDialog',
                    title: 'Edit details',
                    submitLabel: 'Save',
                    fields: [
                        {
                            name: 'projectManager',
                            label: 'Project Manager',
                            type: 'user',
                            filter: (u) => u.projectManager,
                            initial: task.projectManager,
                            required: true,
                        },
                        // Watching gives full fields, so AV isn't offered (6j-6)
                        { name: 'watchers', label: 'Watchers', type: 'users', filter: (u) => !u.avOnly, initial: task.watchers || [] },
                        ...(isPost ? [] : [{
                            name: 'videoHeading',
                            type: 'heading',
                            label: 'For every language',
                            help: `Changing these changes them on all languages of ${task.contentTitle}.`,
                        },
                        {
                            name: 'category',
                            label: 'Category',
                            type: 'select',
                            options: [
                                { value: '', label: 'None' },
                                // A hidden category stays listed while this file still has it
                                ...categories
                                    .filter((c) => !c.hidden || c.key === task.categoryKey)
                                    .map((c) => ({ value: c.key, label: c.label })),
                            ],
                            initial: task.categoryKey || '',
                        }]),
                        ...(task.contentType === 'VIDEO'
                            ? [
                                  {
                                      name: 'duration',
                                      label: 'Duration',
                                      placeholder: '12:30',
                                      help: 'minutes:seconds or hours:minutes:seconds',
                                      initial: formatDuration(task.durationSeconds),
                                      validate: durationError,
                                  },
                                  {
                                      name: 'sourceUrl',
                                      label: 'Source video link',
                                      placeholder: 'https://',
                                      initial: task.sourceUrl || '',
                                      validate: sourceUrlError,
                                  },
                                  {
                                      name: 'platform',
                                      label: 'Platform',
                                      type: 'select',
                                      help: 'With a Platform, this shows on the Posting calendar.',
                                      options: [{ value: '', label: 'None' }, ...platformChoices(platforms, task.platform)],
                                      initial: task.platform || '',
                                  },
                              ]
                            : []),
                    ],
                    submit: ({ category, duration, sourceUrl, platform, ...vals }) =>
                        TaskTrackingService.update(task.id, {
                            ...v(),
                            ...vals,
                            // Edit post changes these on a post
                            contentFields: isPost ? undefined : contentFieldsFrom(
                                {
                                    category,
                                    duration: duration ?? formatDuration(task.durationSeconds),
                                    sourceUrl: sourceUrl ?? task.sourceUrl,
                                    platform: task.contentType === 'VIDEO' ? platform ?? task.platform ?? '' : undefined,
                                },
                                { always: true },
                            ),
                        }),
                }),
        },
        {
            label: 'Cancel task',
            danger: true,
            open: () =>
                openDialog({
                    usageName: 'TaskCancelDialog',
                    title: 'Cancel task',
                    description: waiting
                        ? `The task closes. ${planFolderLine(detail.plan)}`.trim()
                        : isAudio
                          ? 'The task closes. Its shared folder stays.'
                          : 'The task closes. A new one can be started for this file and language later.',
                    submitLabel: 'Cancel task',
                    submitColor: 'error',
                    fields: [{ name: 'reason', label: 'Reason', multiline: true, required: true }],
                    submit: (vals) => TaskTrackingService.cancel(task.id, { ...v(), reason: vals.reason }),
                }),
        },
    ];

    const postActions = [
        { label: 'Edit post', open: () => setEditingPost(true) },
        {
            label: 'Delete post',
            danger: true,
            open: () =>
                openDialog({
                    usageName: 'PostDeleteDialog',
                    title: 'Delete post',
                    description: `Delete ${task.contentTitle} and its ${detail.post?.strip?.length || 1} language${detail.post?.strip?.length === 1 ? '' : 's'}? It goes to the trash for 30 days.`,
                    submitLabel: 'Delete post',
                    submitColor: 'error',
                    fields: [],
                    submit: async () => {
                        await PostService.remove(detail.post.postId);
                        onChanged && onChanged();
                        refreshTaskSummary();
                        onClose();
                    },
                }),
        },
    ];

    const deleteAction = {
        label: 'Delete task',
        danger: true,
        open: () =>
            openDialog({
                usageName: 'TaskDeleteDialog',
                title: 'Delete task',
                description: noFile
                    ? `The task and its History are removed for good. ${planFolderLine(detail.plan)}`.trim()
                    : `The task and its History are removed for good. ${task.languageLabel}'s files and the editor stay as they are.`,
                submitLabel: 'Delete task',
                submitColor: 'error',
                fields: [
                    { name: 'confirm', label: "I understand this can't be undone", type: 'checkbox', required: true },
                ],
                submit: async () => {
                    await TaskTrackingService.remove(task.id, task.version);
                    onChanged && onChanged();
                    refreshTaskSummary();
                    onClose();
                },
            }),
    };
    // A done or cancelled task has nobody left to assign and refuses edits. Stages send back and move
    const closed = task?.overallStatus === 'COMPLETED' || task?.overallStatus === 'CANCELLED';
    const openActions = closed ? [] : pmActions;
    const menuActions = !perms.canManage
        ? []
        : isPost
          ? [...openActions, ...postActions]
          : [...openActions, deleteAction];

    const sendBackLink = perms.canManage && task?.overallStatus === 'COMPLETED' && earlier.length > 0;
    return { menuActions, moveFor, openSendBack, sendBackLink, openPostingDate, canSetPostDate };
};

export default usePmActions;
